import styled from 'styled-components';
import {
  CUSTOM_DESCRIPTION_PREFIX,
  parseCustomDescription,
  isCustomDescription,
  parseStrategyDescription,
} from '@paradigm/features/src/unified-rfqs/ui/rfqs/rfqs-functions';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import LegsDescription from './LegsDescription';

interface Props {
  readonly children: string;
  readonly maxNumLegs?: number;
  readonly className?: string;
  readonly strategyNameMaxLength?: number;
  readonly whiteSpace?: string;
}

export function StrategyDescription({ children, ...props }: Props) {
  if (isCustomDescription(children))
    return (
      <CustomStrategyDescription {...props}>
        {children}
      </CustomStrategyDescription>
    );

  return (
    <DefinedStrategyDescription {...props}>
      {children}
    </DefinedStrategyDescription>
  );
}

export function DefinedStrategyDescription({
  children,
  maxNumLegs = 2,
  strategyNameMaxLength = 32,
  className,
  whiteSpace = 'pre-wrap',
}: Props) {
  const strategyLines = parseStrategyDescription(children);
  const [strategyName, ...legs] = strategyLines;
  const hasHedgeLegs = legs.length > 0;
  // if strategy name is bigger than a character threshold, we display it with 2 lines
  const isTwoLineStrategy =
    strategyName != null && strategyName.length >= strategyNameMaxLength;
  const numStrategyLines = isTwoLineStrategy ? 2 : 1;

  const showStrategyEllipsis = hasHedgeLegs && maxNumLegs === numStrategyLines;

  return (
    <DefinedStrategyDescriptionContainer className={className}>
      <StrategyContent
        className="strategy-description-content"
        whiteSpace={whiteSpace}
      >
        {strategyName}
        {showStrategyEllipsis ? '...' : ''}
      </StrategyContent>
      <LegsDescription legs={legs} maxLength={maxNumLegs - numStrategyLines} />
    </DefinedStrategyDescriptionContainer>
  );
}

export function CustomStrategyDescription({
  children,
  className,
  maxNumLegs = 2,
}: Props) {
  const legs = parseCustomDescription(children);

  return (
    <CustomStrategyDescriptionContainer className={className}>
      <div>{CUSTOM_DESCRIPTION_PREFIX}</div>
      <LegsDescription legs={legs} maxLength={maxNumLegs} />
    </CustomStrategyDescriptionContainer>
  );
}

const StrategyContent = styled.div<{ whiteSpace: string }>`
  white-space: ${(props) => props.whiteSpace};
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    text-align: end;
  }
`;
const DefinedStrategyDescriptionContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  max-height: 40px;
  overflow: visible;
  align-items: start;
  line-height: 1.3rem;
`;

export const CustomStrategyDescriptionContainer = styled.div`
  display: inline-flex;
  overflow: visible;
  max-height: 40px;
  grid-gap: 0.5rem;
`;
